import { useEffect, useState } from 'react';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import { sameUsGamePropTarget, usGamePropTarget } from '@/lib/polymarketUs/gamePropTarget';
import { sameUsPlayerTarget, usPlayerTarget } from '@/lib/polymarketUs/playerTarget';
import type { UsDiscoveryMarket, UsMarketClient } from '@/lib/polymarketUs/marketClient';

/** One account-free read for the rendered page. Keep exact catalogue identity
 * and original evidence clocks; a fresh HTTP response never refreshes a book. */
export async function readUsPlayerQuotes(client: Pick<UsMarketClient, 'listPublicMarkets'>,
  rows: readonly UsDiscoveryMarket[], signal?: AbortSignal, gameProps = false): Promise<readonly UsDiscoveryMarket[]> {
  if (!rows.length) return [];
  const next = await client.listPublicMarkets({ slugs: rows.map(m => m.marketSlug) }, signal);
  return next.markets.filter(m => {
    const prior = rows.find(r => r.marketSlug === m.marketSlug), target = usPlayerTarget(m);
    if (gameProps) {
      const original = prior && usGamePropTarget(prior), candidate = usGamePropTarget(m);
      return !!prior && !!original && !!candidate && m.gameKey === prior.gameKey
        && m.classification.revision === prior.classification.revision && sameUsGamePropTarget(original, candidate);
    }
    const original = prior && usPlayerTarget(prior);
    return prior && target && original && m.gameKey === prior.gameKey
      && m.classification.revision === prior.classification.revision && sameUsPlayerTarget(original, target);
  });
}

export function useUsPlayerQuotes(rows: readonly UsDiscoveryMarket[], enabled: boolean, gameProps = false): readonly UsDiscoveryMarket[] {
  const authenticated = useAuthStore(s => s.status === 'authenticated');
  const user = useAuthStore(s => s.user);
  const epoch = getAuthSessionEpoch();
  // Catalogue refreshes may change observation times without changing identity.
  // Do not re-arm the quote loop or fan out reads for those price-only changes.
  const identity = JSON.stringify(rows.map(m => [m.gameKey, m.classification.revision, gameProps ? usGamePropTarget(m) : usPlayerTarget(m)]));
  const key = enabled && authenticated && user?.orgId && rows.length ? `${epoch}|${user.id}|${user.orgId}|${identity}` : null;
  const [client] = useState(createUsBrowserMarketClient);
  const [state, setState] = useState<{ key: string; markets: readonly UsDiscoveryMarket[] } | null>(null);
  useEffect(() => {
    if (!key) return;
    // `identity` locks the immutable membership for this arm; changed membership
    // tears it down and excludes every late response, including A-B-A.
    const selected = rows;
    let cancelled = false, pending = false, visibilityEpoch = 0, controller: AbortController | null = null;
    const isVisible = () => document.visibilityState !== 'hidden';
    const load = async () => {
      if (cancelled || pending || !isVisible()) return;
      pending = true;
      const visibleEpoch = visibilityEpoch;
      const request = new AbortController(); controller = request;
      const timeout = setTimeout(() => request.abort(), 2000);
      try {
        const markets = await readUsPlayerQuotes(client, selected, request.signal, gameProps);
        if (!cancelled && !request.signal.aborted && visibleEpoch === visibilityEpoch && isVisible()) setState({ key, markets });
      } catch {
        if (!cancelled && visibleEpoch === visibilityEpoch) setState({ key, markets: [] });
      } finally { clearTimeout(timeout); pending = false; if (controller === request) controller = null; }
    };
    const visibility = () => {
      visibilityEpoch++;
      controller?.abort();
      setState({ key, markets: [] });
      if (isVisible()) void load();
    };
    void load();
    const timer = setInterval(() => void load(), 1000);
    document.addEventListener('visibilitychange', visibility);
    return () => { cancelled = true; controller?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', visibility); client.invalidate(); };
    // Membership identity is deliberately the dependency, not a new row array
    // emitted by every catalogue refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, client, gameProps]);
  return state?.key === key ? state.markets : [];
}
